import { z } from 'zod';

import { pathOrUrl } from '../../utils';
import {
    CATEGORY_BASEBALL,
    CATEGORY_BASKETBALL,
    CATEGORY_FOOTBALL,
    CATEGORY_MULTISPORT,
    CATEGORY_POKEMON,
} from '../constants/pack';
import { CardSchema } from './Card';

export const PackCategorySchema = z.preprocess(
    (value) => (typeof value === 'string' ? value.toLowerCase() : value),
    z.enum([
        CATEGORY_BASEBALL,
        CATEGORY_BASKETBALL,
        CATEGORY_FOOTBALL,
        CATEGORY_MULTISPORT,
        CATEGORY_POKEMON,
    ])
);

export const PackTierSchema = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]);

export const PackVariantSchema = z.enum(['standard', 'gold']);
export const PackTypeSchema = z.enum(['repack', 'wax']);
const RepackTypeSchema = z.literal('repack').default('repack');

const BasePackSchema = z.object({
    color: z.string().optional(),
    username: z.string().optional(),
    cards: z.array(CardSchema),
    variant: PackVariantSchema.optional(),
    productName: z.string().optional(), // e.g., "Chrome", "Stadium Club"
});

// Wax pack with custom artwork
const PackWaxSchema = BasePackSchema.extend({
    type: z.literal('wax'),
    image: pathOrUrl, // custom artwork is required for wax packs
});

const PackInstantWaxSchema = BasePackSchema.extend({
    type: z.literal('instant-wax'),
    image: pathOrUrl, // custom artwork is required for instant-wax packs
});

// Legacy support: packs with image but no explicit type
const PackWithImageSchema = BasePackSchema.extend({
    type: RepackTypeSchema,
    image: pathOrUrl,
    category: PackCategorySchema.optional(),
    tier: PackTierSchema.optional(),
});

const PackWithCategoryTierSchema = BasePackSchema.extend({
    type: RepackTypeSchema,
    image: pathOrUrl.optional(),
    category: PackCategorySchema,
    tier: PackTierSchema,
});

export const PackSchema = z.union([
    PackWaxSchema,
    PackInstantWaxSchema,
    PackWithImageSchema,
    PackWithCategoryTierSchema,
]);

export type PackCategory = z.infer<typeof PackCategorySchema>;
export type PackTier = z.infer<typeof PackTierSchema>;
export type PackVariant = z.infer<typeof PackVariantSchema>;
export type PackType = z.infer<typeof PackTypeSchema>;
export type Pack = z.infer<typeof PackSchema>;
